import Phaser from 'phaser';
import {
  STAR_COLORS,
  chapterLevelLabel,
  chapterUnlocked,
  displayStars,
  findBorder,
  hambiLabel,
} from '../../config/creator';
import { HUB_MENU_TRACK } from '../../config/levels';
import { chapterWins } from '../../config/exploreRun';
import { saveStorage } from '../saveStorage';
import { accountClient } from '../../account/accountUi';
import {
  chapterPlay,
  exploreChapterGet,
  exploreGet,
  explorePlay,
  type ExploreChapter,
} from '../../account/explore';
import { ScrollList } from '../../ui/scrollList';
import { drawStarfield } from '../starfield';
import { getMusicService } from '../audio/MusicService';
import { creatorFlash, creatorHeader } from '../creator/creatorUi';
import { startExploreRun } from './ExploreScene';

const LIST_X = 190;
const LIST_Y = 200;
const LIST_W = 900;
const LIST_H = 500;
const ROW_H = 72;
const ROW_GAP = 10;

export const LOCKED_LEVEL_TEXT = 'Beat the level before it first';

/**
 * A published chapter in Explore (spec 2026-11-03 §5): its levels in order,
 * labelled the way its maker chose (Episode / Level / Stage, then I / 1 / A).
 * The first is open and each win opens the next — on this device. A level
 * that has since gone from Explore reads "Unavailable" and is stepped over.
 */
export class ExploreChapterScene extends Phaser.Scene {
  private code = '';
  private busy = false;
  private list?: ScrollList;
  /** The chapter as loaded (read by tests). */
  chapter: ExploreChapter | null = null;

  constructor() {
    super({ key: 'ExploreChapterScene' });
  }

  init(data: { code?: string }): void {
    if (data?.code) this.code = data.code;
    this.sys.settings.data = {};
  }

  create(): void {
    this.busy = false;
    this.chapter = null;
    drawStarfield(this, 1280, 720);
    creatorHeader(this, 'CHAPTER', () => this.scene.start('ExploreScene', { tab: 'chapters' }));
    const music = getMusicService(this);
    music.playTrack(HUB_MENU_TRACK);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      music.stop();
      this.list?.destroy();
      this.list = undefined;
    });
    const status = this.add
      .text(640, 380, 'Loading…', { fontSize: '26px', color: '#e8f4f1', fontStyle: 'bold' })
      .setOrigin(0.5)
      .setName('chapter-status');
    const client = accountClient();
    if (!client || !this.code) {
      status.setText('Explore needs the online servers');
      return;
    }
    void exploreChapterGet(client, this.code).then((r) => {
      if (!this.sys.isActive()) return;
      if (!r.ok) {
        status.setText(r.error);
        return;
      }
      status.destroy();
      this.chapter = r.value;
      this.draw(r.value);
    });
  }

  private draw(ch: ExploreChapter): void {
    const border = findBorder(ch.border).color;
    this.add.rectangle(640, 440, 1180, 540).setStrokeStyle(6, border, 1);
    this.add
      .text(640, 110, ch.name, { fontSize: '36px', color: '#ffffff', fontStyle: 'bold' })
      .setOrigin(0.5)
      .setName('chapter-name');
    const stars = displayStars(ch.plays, ch.starOverride);
    this.add
      .text(
        640,
        152,
        `by ${ch.username}   #${ch.code}   ${'★'.repeat(stars.count)}${'☆'.repeat(5 - stars.count)}   ${hambiLabel(ch.hambis)}`,
        { fontSize: '18px', color: STAR_COLORS[stars.color] },
      )
      .setOrigin(0.5);

    const won = chapterWins(saveStorage(), ch.code);
    const open = chapterUnlocked(ch.levels, won);
    const list = new ScrollList(this, {
      x: LIST_X,
      y: LIST_Y,
      width: LIST_W,
      height: LIST_H,
      barX: LIST_X + LIST_W + 18,
      depth: 1,
    });
    this.list = list;
    ch.levels.forEach((lv, i) => {
      const y = i * (ROW_H + ROW_GAP) + ROW_H / 2;
      const available = lv.name !== null;
      const isOpen = open[i];
      const beaten = won.includes(lv.code);
      const plate = this.add
        .rectangle(LIST_W / 2, y, LIST_W, ROW_H, isOpen ? 0x1c2130 : 0x101218, 0.95)
        .setStrokeStyle(2, border, isOpen ? 1 : 0.35)
        .setInteractive({ useHandCursor: isOpen })
        .setName(`chapter-level-${i}`);
      const label = this.add
        .text(24, y, chapterLevelLabel(ch.format, ch.order, i), {
          fontSize: '22px',
          color: isOpen ? '#ffd23f' : '#5c6168',
          fontStyle: 'bold',
        })
        .setOrigin(0, 0.5)
        .setName(`chapter-label-${i}`);
      const name = this.add
        .text(250, y, available ? (lv.name ?? '') : 'Unavailable', {
          fontSize: '22px',
          color: isOpen ? '#ffffff' : '#5c6168',
          fontStyle: available ? 'bold' : 'italic',
        })
        .setOrigin(0, 0.5)
        .setName(`chapter-level-name-${i}`);
      const mark = this.add
        .text(LIST_W - 24, y, beaten ? '✓' : isOpen ? '▶' : available ? '🔒' : '', {
          fontSize: '24px',
          color: beaten ? '#5bd35b' : '#ffffff',
        })
        .setOrigin(1, 0.5)
        .setName(`chapter-mark-${i}`);
      list.content.add([plate, label, name, mark]);
      plate.on('pointerup', (ptr: Phaser.Input.Pointer) => {
        if (list.dragged || !list.inView(ptr)) return;
        if (!available) {
          creatorFlash(this, 'That level is no longer in Explore');
          return;
        }
        if (!isOpen) {
          creatorFlash(this, LOCKED_LEVEL_TEXT);
          return;
        }
        void this.play(lv.code);
      });
    });
    list.setContentHeight(ch.levels.length * (ROW_H + ROW_GAP));
  }

  /** Download the level, count a play of it and of the chapter, start the run. */
  private async play(levelCode: string): Promise<void> {
    const client = accountClient();
    if (this.busy || !client || !this.chapter) return;
    this.busy = true;
    const r = await exploreGet(client, levelCode);
    if (!this.sys.isActive()) return;
    if (!r.ok) {
      this.busy = false;
      creatorFlash(this, r.error);
      return;
    }
    const chapterCode = this.chapter.code;
    if (!startExploreRun(this, r.value, chapterCode)) {
      this.busy = false;
      creatorFlash(this, "This level can't be played");
      return;
    }
    void explorePlay(client, r.value.code);
    void chapterPlay(client, chapterCode);
  }
}
